import React, { useEffect, useCallback } from 'react';
import { Typography, Paper, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { AppStoreState } from 'types';
import { HwtButton } from '../../Buttons/HwtButton';
import { getWeekStartingIndex, getLessonPlanWeekIndex } from 'util/lesson-plan-util';
import { NoInstruction } from 'containers/LessonPlanner/components/NoInstruction';
import { setLessonPlanToDate } from 'state/modules/lessonPlanner/thunks';
import { translate } from 'translations/translator';
import { ViewTeachingOrderTitleSection } from './ViewTeachingOrderTitleSection';
import { ViewTeachingOrderSections } from './ViewTeachingOrderSections';
import { ViewTeachingOrderActionNav } from './ViewTeachingOrderActionNav';
import { formatUiDate } from 'util/date-util';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { ModalPadding } from 'styles/style-constants';

const t = key => translate('lessonPlanner', key);

export type Props = {
  handleNextClick: () => void;
  handlePreviousClick: () => void;
  lessonPlan: any;
};

export const ViewTeachingOrderModal: React.FC<Props> = ({
  handleNextClick,
  handlePreviousClick,
  lessonPlan
}: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch() as any;
  const [, { closeModal }] = useModal();

  const currentlessonPlanWeek = useSelector(
    ({ lessonPlanner }: AppStoreState) => lessonPlanner.currentlessonPlanWeek
  );

  const resetCurrentWeek = useCallback(() => {
    const index = getLessonPlanWeekIndex(lessonPlan.lessons);

    if (index >= 0) {
      dispatch(setLessonPlanToDate(Math.ceil(index / 5), index % 5));
      return;
    }
    dispatch(setLessonPlanToDate(1, 0));
    return;
  }, [dispatch, lessonPlan.lessons]);

  // reset to current week on component unmount
  useEffect(() => {
    return () => {
      resetCurrentWeek();
    }; // eslint-disable-next-line
  }, []);

  const getLessons = () => {
    const start = getWeekStartingIndex(currentlessonPlanWeek);
    return lessonPlan?.lessons.slice(start, start + 5);
  };

  const lessons = getLessons();
  return (
    <Box className={classes.container}>
      <ViewTeachingOrderActionNav
        handleNextClick={handleNextClick}
        handlePreviousClick={handlePreviousClick}
        currentlessonPlanWeek={currentlessonPlanWeek}
      />
      <Box className={classes.calendarGrid}>
        {lessons?.map((x, xIdx) => (
          <Box key={`lesson-${xIdx}`} display="flex" flexDirection="column" margin="0px 16px">
            <Typography align="center" variant="h6">
              {x?.dayOfWeek ?? ''}
            </Typography>
            <Typography
              style={x.lessonDate ? { textAlign: 'center' } : { marginBottom: '31px' }}
              variant="subtitle2"
            >
              {x.lessonDate ? formatUiDate(new Date(x.lessonDate)) : ''}
            </Typography>
            {x.lessons?.length ? (
              x.lessons?.map((l, lIdx) => {
                return (
                  <Paper
                    key={`card-${xIdx}-${lIdx}`}
                    elevation={2}
                    style={{ width: '190px', height: 'fit-content', marginBottom: '10px' }}
                  >
                    <Box border="solid 1px #A6A6A6" borderRadius={5} margin={0.5} padding={1}>
                      <ViewTeachingOrderTitleSection lesson={l} lessons={x.lessons} />

                      {l?.sections?.map((section, sIdx) => (
                        <ViewTeachingOrderSections
                          key={`lesson-${lIdx}-section-${sIdx}`}
                          section={section}
                        />
                      ))}
                    </Box>
                  </Paper>
                );
              })
            ) : (
              <Paper
                elevation={2}
                style={{ width: '190px', height: 'fit-content', marginBottom: '10px' }}
              >
                <NoInstruction
                  maybeDraggableProps={{}}
                  allowInstruction={x?.allowInstruction}
                  className={classes.noInstruction}
                />
              </Paper>
            )}
          </Box>
        ))}
      </Box>
      <Box display="flex" flex={1} flexDirection="column" className={classes.cancel}>
        <HwtButton
          variant="outlined"
          onClick={closeModal}
          style={{ margin: '15px', width: '106px' }}
        >
          {t('close')}
        </HwtButton>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  cancel: {
    height: '90px',
    padding: '0 32px',
    borderTop: '1px solid #DBDBDB',
    alignItems: 'flex-end'
  },
  container: {
    ...ModalPadding,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex !important',
    flexDirection: 'column',
    marginTop: '0 !important',
    padding: '0 !important',
    height: 'max-content !important',
    width: 'max-content !important',
    backgroundColor: '#fff'
  },
  calendarGrid: () => ({
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    paddingTop: '25px',
    paddingBottom: '50px',
    '::-webkit-scrollbar': {
      display: 'none'
    }
  }),
  noInstruction: () => ({
    border: 'solid 1px #A6A6A6',
    height: '300px',
    borderRadius: '5px',
    margin: '0.5px',
    padding: '1px',
    textAlign: 'center'
  })
}));
